import { Link } from 'react-router-dom';
import { pct } from '../lib/format';
import { when } from '../lib/labels';
import { Section, Table, useApi, ErrorBox } from '../ui/kit';

// Domain protection: per sending domain and mailbox, what Outlevo's guard and the daily blocklist check see, and what
// they paused. Read-only: a switched-off mailbox is released under Stop spending, a paused campaign started on its page.

type Listing = { list: 'spamhaus_dbl' | 'surbl' | 'spamhaus_zen'; target: string; status: 'listed' | 'clean' | 'unknown'; meaning: string | null };
type Mailbox = {
  id: string; email: string; connected: boolean; warmup: { status: string; reputation: number | null; managed_status: string | null; ready_expected_at: string | null };
  sends_today: number; daily_cap: number | null; sent_7d: number; hard_bounces_7d: number; bounce_rate_7d: number | null; complaints_7d: number; unsubscribes_7d: number;
  paused: { reason: string; since: string } | null;
};
type Domain = {
  domain: string; managed_status: string | null; blacklist: { status: 'unchecked' | 'clean' | 'listed' | 'unknown'; checked_at: string | null; listed_since: string | null; listings: Listing[] };
  mailboxes: Mailbox[];
};
export type Health = {
  thresholds: { window: number; min_sample: number; hard_bounce_rate: number; unsubscribe_rate: number; campaign_complaints: number; mailbox_complaints: number };
  domains: Domain[];
  paused_campaigns: Array<{ id: string; name: string; paused_at: string; reason: string | null; checks: string[] }>;
};

const LIST: Record<Listing['list'], string> = { spamhaus_dbl: 'Spamhaus DBL', surbl: 'SURBL', spamhaus_zen: 'Spamhaus ZEN' };
const BLACKLIST: Record<Domain['blacklist']['status'], [string, string]> = {
  clean: ['On no blocklist', 'good'], listed: ['On a blocklist', 'bad'], unknown: ['Blocklists did not answer', 'neutral'], unchecked: ['Not checked yet', 'neutral'],
};

function warmup(w: Mailbox['warmup']) {
  if (w.managed_status === 'warming') return `Warming up${w.ready_expected_at ? `, ready around ${new Date(w.ready_expected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}`;
  const base = w.status === 'active' ? 'On' : w.status === 'inactive' ? 'Off' : 'Unknown';
  return w.reputation !== null ? `${base} · ${w.reputation}% reputation` : base;
}

/** The Sending health section of the Campaigns page. */
export function SendingHealth() {
  const q = useApi<Health>('/v1/sending/health');
  if (q.error) return <Section title="Sending health"><ErrorBox error={q.error} retry={() => q.refetch()} /></Section>;
  if (!q.data) return <Section title="Sending health"><p className="muted" role="status">Loading…</p></Section>;
  const t = q.data.thresholds;
  return (
    <Section title="Sending health">
      <p className="small muted">
        Outlevo pauses a campaign over {pct(t.hard_bounce_rate)} hard bounces or {pct(t.unsubscribe_rate)} unsubscribes in its last {t.window} sends (judged from {t.min_sample}), or at a spam complaint;
        a mailbox is switched off at the same rates or {t.mailbox_complaints} complaints. Domains are checked daily against Spamhaus and SURBL. Only a person starts sending again.
      </p>
      {q.data.paused_campaigns.length ? (
        <div className="error" role="alert">
          <strong>Paused to protect your domain</strong>
          <ul className="tight">{q.data.paused_campaigns.map((c) => <li key={c.id}><Link to={`/campaigns/${c.id}`}>{c.name}</Link>: {(c.reason ?? '').replace(/^Paused to protect your domain: /, '')} ({when(c.paused_at)})</li>)}</ul>
        </div>
      ) : null}
      {q.data.domains.length ? q.data.domains.map((d) => {
        const [text, tone] = BLACKLIST[d.blacklist.status];
        const listed = d.blacklist.listings.filter((l) => l.status === 'listed');
        return (
          <div key={d.domain} style={{ marginTop: 16 }}>
            <h3>{d.domain} <span className={`chip chip-${tone}`}>{text}</span></h3>
            <p className="small muted">
              {listed.length ? `Listed on ${listed.map((l) => `${LIST[l.list]}${l.list === 'spamhaus_zen' ? ` (${l.target})` : ''}${l.meaning ? `: ${l.meaning}` : ''}`).join('; ')}${d.blacklist.listed_since ? ` since ${when(d.blacklist.listed_since)}` : ''}. ` : ''}
              {d.blacklist.checked_at ? `Blocklists checked ${when(d.blacklist.checked_at)}.` : 'Blocklists are checked once a day.'}
              {d.managed_status ? ' Managed by Outlevo.' : ''}
            </p>
            <Table rows={d.mailboxes} caption={`Mailboxes on ${d.domain}`} empty="No mailbox on this domain yet." columns={[
              { key: 'email', label: 'Mailbox' },
              { key: 'warmup', label: 'Warm-up', render: (m) => warmup(m.warmup) },
              { key: 'sends_today', label: 'Sends today', render: (m) => `${m.sends_today}${m.daily_cap ? ` of ${m.daily_cap}` : ''}` },
              { key: 'bounce_rate_7d', label: 'Bounces (7 days)', render: (m) => (m.sent_7d ? `${pct(m.bounce_rate_7d)} of ${m.sent_7d}` : '—') },
              { key: 'complaints_7d', label: 'Complaints (7 days)', render: (m) => String(m.complaints_7d) },
              { key: 'unsubscribes_7d', label: 'Unsubscribes (7 days)', render: (m) => String(m.unsubscribes_7d) },
              { key: 'paused', label: 'Paused', render: (m) => (m.paused ? <span><span className="chip chip-bad">switched off</span> {m.paused.reason} <Link to="/kill-switches">Release…</Link></span> : m.connected ? 'No' : 'Not connected') },
            ]} />
          </div>
        );
      }) : <p className="muted">No sending domain yet: set sending up, or connect your own Smartlead.</p>}
    </Section>
  );
}

/** On a campaign the guard paused: why, what to check, and that a person starts it again. */
export function ProtectionNotice({ p }: { p: { reason: string; checks: string[]; scope: string } | null | undefined }) {
  if (!p) return null;
  return (
    <div className="error" role="alert">
      <strong>{p.reason}.</strong> Here is what to check before starting it again:
      <ul className="tight">{p.checks.map((c) => <li key={c}>{c}</li>)}</ul>
      {p.scope === 'campaign' ? 'Then press Start sending.' : <>A mailbox it sends from is switched off: an owner or admin releases it under <Link to="/kill-switches">Stop spending</Link>, then press Start sending.</>}
    </div>
  );
}
